import "./DisplayCompanies.css";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Button from "../../../components/common/button/Button";
import Table from "../../../components/common/table/Table";
import { openModal } from "../../../redux/modal/modalActions";
import { useDispatch } from "react-redux";
import { getAllCompanies } from "../../../apis/companiesApis/getAllCompanies";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import { routeNames } from "../../../consts/routesNames";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { createMenuItem } from "../../../helpers/menuHelper";
import ConfirmationBox from "../../../components/common/confirmationBox/ConfirmationBox";
import { deleteCompany } from "../../../apis/companiesApis/deleteCompany";
import { PERMISSIONS } from "../../../auth/utils/permissionCodes";
import { useAuth } from "../../../auth/hooks/usePermissions";
import { useCrudPermissions } from "../../../auth/hooks/useCrudPermissions";
import { useUpdateOwnership } from "../../../auth/hooks/useUpdateOwnership";
import { emptyTableMessageForLinkedContact, LINKED_CONTACT_TABLE_EMPTY } from "../../../consts/tableEmptyMessages";
import { formatCreatedByDisplay } from "../../../utils/tableDisplayUtils";

function DisplayCompanies({ homePageDisplay }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    canCreate: canCreateCompanies,
    canUpdate: canUpdateCompanies,
    canDelete: canDeleteCompanies,
  } = useCrudPermissions(PERMISSIONS.COMPANIES);
  const { canUpdateOwnedRecord } = useUpdateOwnership();
  const { contactId } = useAuth();

  const [companiesData, setCompaniesData] = useState([]);

  const companiesEmptyMessage = emptyTableMessageForLinkedContact(contactId, companiesData.length, LINKED_CONTACT_TABLE_EMPTY.COMPANIES);

  const getCompanies = useCallback(async () => {
    try {
      const allCompaniesRes = await getAllCompanies();
      if (allCompaniesRes.error_code === 2 || allCompaniesRes.error_code === 3) {
        dispatch(openModal(<ResponseModal text={allCompaniesRes.error_message} isError={true} />));
      } else {
        let companiesArray = [];
        if (allCompaniesRes.res.data.length > 0) {
          companiesArray = allCompaniesRes.res.data.map((company) => ({
            ...company,
            address: `${company.street_number ?? "-"} ${company.street_name ?? "-"}, ${company.city ?? "-"}, ${company.state ?? "-"} ${
              company.zip_code ?? "-"
            }`,
            category_name: company.category?.name ?? "",
            created_by_display: formatCreatedByDisplay(company.created_by),
          }));
        }
        setCompaniesData(companiesArray);
      }
    } catch (error) {
      console.error("Error fetching companies :", error);
    }
  }, [dispatch]);

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  const tableHeaders = [
    {
      key: "id",
      name: "ID",
    },
    {
      key: "name",
      name: "Name",
    },
    {
      key: "email",
      name: "Email",
    },
    {
      key: "address",
      name: "Full Address",
    },
    {
      key: "category_name",
      name: "Category",
    },
    {
      key: "website",
      name: "Website",
    },
    {
      key: "created_by_display",
      name: "Created By",
    },
  ];

  const errorDispatcher = (message) => {
    alert(message);
    dispatch(openModal(<ResponseModal text={message} isError={true} />));
  };

  const HandleCompaniesDetailsClick = (rowData) => {
    navigate(`${routeNames.COMPANY_DETAILS}?id=${rowData.id}`, { state: { data: rowData } });
  };

  const canEditCompany = (rowData) =>
    canUpdateOwnedRecord(rowData.created_by, canUpdateCompanies);

  const editCompany = (rowData) => {
    if (!canEditCompany(rowData)) return;
    navigate(`${routeNames.EDIT_COMPANY.split(":")[0]}${rowData.id}`, {
      state: { data: rowData },
    });
  };

  const confirmDeleteCompany = (rowData) => {
    if (!canDeleteCompanies) return;
    dispatch(
      openModal(
        <ConfirmationBox
          msg={`Are you sure you want to delete "${rowData.name}"`}
          saveText={"OK"}
          discardText={"Cancel"}
          saveAction={async () => {
            try {
              await deleteCompany(rowData.id);
              errorDispatcher("Deleted Successfully");
              getCompanies();
            } catch (error) {
              console.error("Error deleting company:", error);
              errorDispatcher("Failed to delete the company.");
            }
          }}
        />,
      ),
    );
  };

  const HandleAddNewCompanyClick = () => {
    if (!canCreateCompanies) return;
    navigate(routeNames.ADD_COMPANY);
  };

  const getCompanyRowActions = (row) =>
    [
      canEditCompany(row) && createMenuItem("Edit", editCompany, <EditIcon />),
      canDeleteCompanies &&
        createMenuItem("Delete", confirmDeleteCompany, <DeleteIcon />),
    ].filter(Boolean);

  return (
    <>
      <div className={homePageDisplay ? "main-container-displayCompanies-homePageDisplay" : "main-container-displayCompanies"}>
        {!homePageDisplay ? (
          <div className="btns-main-container-displayCompanies">
            {canCreateCompanies && (
              <div className="add-btn-container-displayCompanies">
                <Button className={"add-btn-displayCompanies"} buttonText={"Add New Company"} onClick={HandleAddNewCompanyClick} />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
        <div className="table-main-container-displayCompanies">
          <Table
            searchable={homePageDisplay ? false : true}
            tableHeight={homePageDisplay ? "450px" : "650px"}
            headers={tableHeaders}
            rows={companiesData}
            onRowClick={HandleCompaniesDetailsClick}
            getRowActions={getCompanyRowActions}
            emptyMessage={companiesEmptyMessage}
          />
        </div>
      </div>
    </>
  );
}

export default DisplayCompanies;
